import {ApolloLink} from '@apollo/client';
import {noop} from '@sail/utils';
import determineUnknownErrorType from 'src/internal/revamp/fetchUtils/determineUnknownErrorType';
import DataNetworkError from 'src/internal/revamp/graphql/DataNetworkError';
import {reportRuntimeError} from 'src/internal/revamp/telemetry/internalReporting';

import type {FetchResult} from '@apollo/client';
import type {InternalOperationContext} from 'src/internal/revamp/apollo/types';
import type {GqlError} from 'src/internal/revamp/graphql/GqlError';
import type {GraphQlOperationErrors} from 'src/internal/revamp/graphql/types/errors';
import type {Fetch} from 'src/internal/shared/fetch/types';
import type {
  DataOfDoc,
  ErrorExtensionsOfDoc,
  GraphQlDocument,
  TransformedQueryDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';

type ExecutorSubscriber<
  TDocumentNode extends GraphQlDocument<any, any, any, any>,
> = {
  next: (result: {
    data: DataOfDoc<TDocumentNode> | null;
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null;
  }) => void;
  complete: () => void;
};

export type ApolloLinkExecuteContext = Omit<InternalOperationContext, 'fetch'>;

export default class ApolloLinkExecutor {
  private link: ApolloLink;
  private fetchFunction: Fetch;

  constructor(link: ApolloLink, fetchFunction: Fetch) {
    this.link = link;
    this.fetchFunction = fetchFunction;
  }

  execute<TDocumentNode extends GraphQlDocument<any, any, any, any>>(
    query: TDocumentNode | TransformedQueryDocument<TDocumentNode>,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
    context: ApolloLinkExecuteContext,
    subscriber: ExecutorSubscriber<TDocumentNode>,
  ): {unsubscribe: () => void} {
    const contextWithFetch: InternalOperationContext = {
      ...context,
      fetch: this.fetchFunction,
    };

    try {
      const subscription = ApolloLink.execute(this.link, {
        query,
        variables,
        context: contextWithFetch,
      }).subscribe({
        next: function apolloLinkExecutorNext(
          result: FetchResult<DataOfDoc<TDocumentNode>>,
        ) {
          const {data, errors} = result;

          if (errors) {
            subscriber.next({
              data: data || null,
              errors: {
                graphQlErrors: errors as unknown as GqlError<
                  ErrorExtensionsOfDoc<TDocumentNode>
                >[],
                clientErrors: [],
                networkErrors: [],
              },
            });
          } else {
            subscriber.next({data: data || null, errors: null});
          }
        },

        // @apollo/client Obsevable types the argument as any, it's important for us to explicitly cast it to unknown.
        error: function apolloLinkExecutorError(error: unknown) {
          if (
            error instanceof DataNetworkError ||
            // DataNetworkError are created by both v1 and v2 terminating links.
            // But given that we aim for @sail/data to be generic, we use the logic inside `determineUnknownErrorType`
            // as a best attempt to determine if the error is a network error.
            determineUnknownErrorType(error) === 'network'
          ) {
            const networkError = new DataNetworkError(
              error instanceof Error ? error.message : String(error),
              -1,
              null,
            );

            subscriber.next({
              data: null,
              errors: {
                graphQlErrors: [],
                clientErrors: [],
                networkErrors: [networkError],
              },
            });
          } else {
            const typeSafeError =
              error instanceof Error ? error : new Error(String(error));

            subscriber.next({
              data: null,
              errors: {
                graphQlErrors: [],
                clientErrors: [typeSafeError],
                networkErrors: [],
              },
            });
          }
        },
        complete: function apolloLinkExecutorComplete() {
          subscriber.complete();
        },
      });

      return {
        unsubscribe: (): void => {
          subscription.unsubscribe();
        },
      };
    } catch (error) {
      reportRuntimeError(context.observabilityFns, error);

      subscriber.next({
        data: null,
        errors: {
          graphQlErrors: [],
          clientErrors: [new Error('Error in ApolloLinkExecutor')],
          networkErrors: [],
        },
      });

      return {
        unsubscribe: noop,
      };
    }
  }
}
